import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [loginId, setLoginId] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email && !loginId) {
      toast.error("ログインIDまたはメールアドレスを入力してください");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { login_id: loginId, email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-7 shadow-xl border border-slate-200">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 leading-tight">パスワードを忘れた</h1>
            <p className="text-xs text-slate-500 mt-0.5">店長(店舗オーナー)向け再設定</p>
          </div>
        </div>

        {sent ? (
          <div className="text-center py-6" data-testid="forgot-sent">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-base font-bold text-slate-900">メールを送信しました</h2>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              ご登録のアドレス宛に再設定リンクをお送りしました。<br />
              受信箱(または迷惑メールフォルダ)をご確認ください。<br />
              <span className="text-[11px] text-slate-400 inline-block mt-2">リンクの有効期限は60分です。</span>
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm text-blue-600 font-bold hover:text-blue-700"
              data-testid="forgot-back-login"
            >
              ログイン画面へ戻る
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <p className="text-xs text-slate-500 leading-relaxed">
              店長(店舗オーナー)の方のパスワード再設定用です。<br />
              <b>ご登録のメールアドレス</b> または <b>ログインID</b> を入力してください。
            </p>
            <div>
              <Label className="text-xs text-slate-600">メールアドレス</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11 text-base"
                autoComplete="email"
                placeholder="例: shop@example.com"
                data-testid="forgot-email"
              />
            </div>
            <div className="text-center text-[11px] text-slate-400">または</div>
            <div>
              <Label className="text-xs text-slate-600">ログインID</Label>
              <Input
                type="text"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                className="mt-1 h-11 text-base"
                autoComplete="username"
                placeholder="例: yasamaru-shop"
                data-testid="forgot-login-id"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-sm font-semibold"
              data-testid="forgot-submit"
            >
              {submitting ? "送信中…" : "再設定リンクを送る"}
            </Button>
            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
              スタッフの方は店長にパスワード再発行を依頼してください。
            </p>
            <Link
              to="/login"
              className="block text-center text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 justify-center w-full"
            >
              <ArrowLeft className="w-3.5 h-3.5" />ログイン画面に戻る
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
